'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  type ConsentState,
  DEFAULT_CONSENT,
  CATEGORIES,
  readConsentFromCookie,
  saveConsentToCookie,
  updateGoogleConsent,
  hasConsentChoice,
} from '@/lib/consent';

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<ConsentState>(DEFAULT_CONSENT);

  useEffect(() => {
    // Show banner if user hasn't made a consent choice yet
    if (!hasConsentChoice()) {
      setVisible(true);
    }

    // Listen for re-open requests (e.g. from footer link)
    const onReopen = () => {
      // Pre-fill with existing preferences if available
      const existing = readConsentFromCookie();
      if (existing) {
        setPreferences(existing);
      } else {
        setPreferences(DEFAULT_CONSENT);
      }
      setShowSettings(true); // open directly to settings
      setVisible(true);
    };
    window.addEventListener('castlopedia:open-consent', onReopen);
    return () => window.removeEventListener('castlopedia:open-consent', onReopen);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all: ConsentState = {
      necessary: true,
      functionality: true,
      analytics: true,
      advertising: true,
      personalization: true,
    };
    saveConsentToCookie(all);
    updateGoogleConsent(all);
    setVisible(false);
  }, []);

  const handleAcceptNecessary = useCallback(() => {
    saveConsentToCookie(DEFAULT_CONSENT);
    updateGoogleConsent(DEFAULT_CONSENT);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    const state: ConsentState = {
      ...preferences,
      necessary: true, // always enforced
    };
    saveConsentToCookie(state);
    updateGoogleConsent(state);
    setVisible(false);
  }, [preferences]);

  const toggleCategory = useCallback((key: keyof ConsentState) => {
    if (key === 'necessary') return; // cannot toggle necessary
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Backdrop when settings are open */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/40 z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {!showSettings ? (
        /* ---- Compact banner: slim full-width bar on mobile, corner card from sm up ----
           Kept deliberately small (short copy, one row of buttons) so it never covers
           the hero CTAs or opening paragraph, and is never the page's LCP element. */
        <div
          role="region"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-[9999] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-full sm:max-w-sm"
        >
          <div className="bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] px-3 py-2.5 sm:rounded-2xl sm:border sm:shadow-2xl sm:p-5 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="hidden sm:block text-sm font-semibold text-gray-900 dark:text-gray-100">
              We value your privacy
            </h2>
            <p className="text-xs text-gray-600 leading-snug sm:mt-1.5 sm:leading-relaxed dark:text-gray-300">
              <span className="sm:hidden">We use cookies for site features, analytics and ads.</span>
              <span className="hidden sm:inline">
                Castlopedia uses cookies to secure the site, provide features, and ensure
                performance. Choose your preference below.
              </span>{' '}
              <a
                href="/privacy"
                aria-label="Learn more about our cookie and privacy policy"
                className="text-forest-green-600 underline hover:text-forest-green-700"
              >
                Read our privacy policy
              </a>
            </p>

            <div className="mt-2 flex items-center gap-2 sm:mt-4 sm:flex-col sm:items-stretch">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="flex-1 min-h-10 rounded-xl bg-forest-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-forest-green-700 transition-colors sm:w-full sm:px-4 sm:py-2.5"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={handleAcceptNecessary}
                className="flex-1 min-h-10 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors sm:w-full sm:px-4 sm:py-2.5 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                <span className="sm:hidden">Necessary only</span>
                <span className="hidden sm:inline">Accept only necessary</span>
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="shrink-0 min-h-10 px-1 text-center text-xs font-medium text-gray-500 underline hover:text-gray-700 transition-colors sm:w-full sm:min-h-0 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span className="sm:hidden">Settings</span>
                <span className="hidden sm:inline">Manage settings</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ---- Settings panel (bottom-right card; inset on mobile so it never overflows) ---- */
        <div
          role="dialog"
          aria-label="Privacy settings"
          className="fixed inset-x-2 bottom-2 z-[9999] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-full sm:max-w-sm"
        >
          <div className="max-h-[calc(100dvh-1rem)] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Privacy settings
              </h2>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close settings"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-1.5 mb-3">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.key}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                >
                  {cat.alwaysActive ? (
                    <div className="flex items-center justify-center w-7 shrink-0">
                      <svg
                        className="h-4 w-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={preferences[cat.key]}
                      onClick={() => toggleCategory(cat.key)}
                      className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        preferences[cat.key]
                          ? 'bg-forest-green-600'
                          : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          preferences[cat.key] ? 'translate-x-3' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                      {cat.title}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-tight dark:text-gray-400">
                      {cat.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="w-full rounded-xl bg-forest-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-forest-green-700 transition-colors"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={handleSavePreferences}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
